import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'

// Slider — a single value picked along a continuous (or stepped) range
// (design/components/bot-config-advanced.html · TemperatureSlider). Built on a
// native <input type="range"> so it inherits role="slider", arrow/Home/End
// keyboard control, and aria-valuemin/max/now for free. Use for bounded numeric
// settings where the relative position matters (temperature, thresholds). For a
// precise number, use NumberInput instead.

export interface SliderProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  /** Visible label above the track; also the accessible name. */
  label?: string
  /** Accessible name when no visible label is shown. Falls back to a localized generic name. */
  'aria-label'?: string
  /** Render the current value (right of the label) and expose it as aria-valuetext. */
  formatValue?: (value: number) => string
  /** Evenly-spaced tick labels under the track (e.g. ["Preciso","Equilibrado","Creativo"]). */
  marks?: string[]
  disabled?: boolean
  id?: string
  className?: string
}

/** Native <input type="range"> for bounded settings where relative position
 *  matters (temperature, thresholds) — for a precise number use NumberInput.
 *  `formatValue` shows the value and feeds aria-valuetext; `marks` adds
 *  evenly-spaced tick labels. Always ends up with an accessible name. */
export function Slider({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  label,
  'aria-label': ariaLabel,
  formatValue,
  marks,
  disabled,
  id,
  className,
}: SliderProps) {
  const { t } = useTranslation('components')
  const autoId = useId()
  const inputId = id ?? autoId
  // Clamp so an out-of-range value can't render a broken track gradient (the
  // native thumb is clamped by the browser, the CSS fill must be too).
  const pct = max === min ? 0 : Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100))
  const valueText = formatValue ? formatValue(value) : undefined
  // Visible label wins as the accessible name; otherwise the explicit aria-label;
  // otherwise a localized generic name so the control is never unnamed.
  const accessibleName = label ?? ariaLabel ?? t('slider.defaultLabel')

  return (
    <div className={cn('flex w-full flex-col gap-1.5', disabled && 'opacity-50', className)}>
      {(label != null || valueText != null) && (
        <div className="flex items-baseline justify-between">
          {label && (
            <label htmlFor={inputId} className="text-[12.5px] font-semibold text-text">
              {label}
            </label>
          )}
          {valueText && (
            <span className="text-[12px] font-bold tabular-nums text-text-2">{valueText}</span>
          )}
        </div>
      )}
      <input
        id={inputId}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label ? undefined : accessibleName}
        aria-valuetext={valueText}
        style={{
          background: `linear-gradient(to right, var(--brand-blue) ${pct}%, var(--input-line) ${pct}%)`,
        }}
        className={cn(
          'h-1.5 w-full cursor-pointer appearance-none rounded-full outline-none',
          'disabled:cursor-not-allowed',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue',
          // thumb — WebKit
          '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:size-[15px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-blue [&::-webkit-slider-thumb]:shadow-[0_1px_3px_rgba(0,0,0,0.25)] [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110',
          // thumb — Firefox
          '[&::-moz-range-thumb]:size-[15px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-brand-blue [&::-moz-range-thumb]:shadow-[0_1px_3px_rgba(0,0,0,0.25)]',
        )}
      />
      {marks && marks.length > 0 && (
        <div className="flex justify-between text-[10.5px] text-text-3" aria-hidden>
          {marks.map((m, i) => (
            <span key={i}>{m}</span>
          ))}
        </div>
      )}
    </div>
  )
}
